import { Phone, Truck, Facebook, Twitter, Instagram, Search, User, ShoppingCart, LogOut, LogIn, UserPlus, Settings, Heart } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { useFavorites } from '@/contexts/FavoritesContext';
import { useUserRole } from '@/hooks/useUserRole';
import { useToast } from '@/hooks/use-toast';
import logo from '@/assets/logo.png';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const TopBar = () => {
  return (
    <div className="bg-secondary text-secondary-foreground py-1.5 md:py-2 px-2 md:px-8">
      <div className="container mx-auto flex justify-between items-center text-xs md:text-sm font-open-sans">
        <div className="flex items-center gap-1 md:gap-2">
          <Phone className="w-3 h-3 md:w-4 md:h-4" />
          <span className="font-semibold">+39 375 7190556</span>
        </div>
        <div className="hidden sm:flex items-center gap-2">
          <span className="text-lg">🚚</span>
          <span className="font-semibold">Spedizioni con corriere espresso</span>
        </div>
      </div>
    </div>
  );
};

const Logo = () => (
  <Link to="/" className="flex items-center">
    <img 
      src={logo} 
      alt="Materialidtf.it" 
      width={288}
      height={96}
      className="h-12 sm:h-16 md:h-20 lg:h-24 w-auto"
    />
  </Link>
);

const Header = () => {
  const { user, signOut } = useAuth();
  const { totalItems } = useCart();
  const { totalFavorites } = useFavorites();
  const { isAdmin, isManagerShop } = useUserRole();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleLogout = async () => {
    await signOut();
    toast({
      title: "Logout effettuato",
      description: "A presto!",
    });
    navigate('/');
  };

  return (
    <header className="bg-primary py-2 md:py-4 px-2 md:px-4">
      <div className="container mx-auto flex justify-between items-center">
        {/* Social Icons */}
        <div className="flex items-center gap-2 md:gap-4">
          <a href="#" className="text-primary-foreground hover:opacity-80 transition-opacity" aria-label="Seguici su Facebook">
            <Facebook className="w-4 h-4 md:w-5 md:h-5" />
          </a>
          <a href="#" className="text-primary-foreground hover:opacity-80 transition-opacity" aria-label="Seguici su Twitter">
            <Twitter className="w-4 h-4 md:w-5 md:h-5" />
          </a>
          <a href="#" className="text-primary-foreground hover:opacity-80 transition-opacity" aria-label="Seguici su Instagram">
            <Instagram className="w-4 h-4 md:w-5 md:h-5" />
          </a>
        </div>

        {/* Logo */}
        <Logo />

        {/* Actions */}
        <div className="flex items-center gap-3 md:gap-6">
          <button className="text-primary-foreground hover:opacity-80 transition-opacity" aria-label="Cerca prodotti">
            <Search className="w-4 h-4 md:w-5 md:h-5" />
          </button>
          
          {user ? (
            <div className="flex items-center gap-2 md:gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1 md:gap-2 text-primary-foreground hover:opacity-80 transition-opacity" aria-label="Menu utente">
                    <User className="w-4 h-4 md:w-5 md:h-5" />
                    <span className="hidden sm:inline font-medium text-sm md:text-base">
                      {user.user_metadata?.full_name || user.email?.split('@')[0] || 'Account'}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild className="hover:!bg-primary hover:!text-primary-foreground focus:!bg-primary focus:!text-primary-foreground">
                    <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer">
                      <User className="w-4 h-4" />
                      <span>Dashboard</span>
                    </Link>
                  </DropdownMenuItem>
                  {(isAdmin || isManagerShop) && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild className="hover:!bg-primary hover:!text-primary-foreground focus:!bg-primary focus:!text-primary-foreground">
                        <Link to="/adminmater" className="flex items-center gap-2 cursor-pointer">
                          <Settings className="w-4 h-4" />
                          <span>Pannello Admin</span>
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="hover:!bg-primary hover:!text-primary-foreground focus:!bg-primary focus:!text-primary-foreground cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    <span>Esci</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="text-primary-foreground hover:opacity-80 transition-opacity" aria-label="Accedi o registrati">
                  <User className="w-4 h-4 md:w-5 md:h-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem asChild className="hover:!bg-primary hover:!text-primary-foreground focus:!bg-primary focus:!text-primary-foreground">
                  <Link to="/auth" className="flex items-center gap-2 cursor-pointer">
                    <LogIn className="w-4 h-4" />
                    <span>Accedi</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="hover:!bg-primary hover:!text-primary-foreground focus:!bg-primary focus:!text-primary-foreground">
                  <Link to="/auth?mode=register" className="flex items-center gap-2 cursor-pointer">
                    <UserPlus className="w-4 h-4" />
                    <span>Registrati</span>
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          
          {user && (
            <Link to="/preferiti" className="text-primary-foreground hover:opacity-80 transition-opacity relative" aria-label={`Preferiti${totalFavorites > 0 ? ` (${totalFavorites})` : ''}`}>
              <Heart className="w-4 h-4 md:w-5 md:h-5" />
              {totalFavorites > 0 && (
                <span className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {totalFavorites > 99 ? '99+' : totalFavorites}
                </span>
              )}
            </Link>
          )}
          
          <Link to="/carrello" className="text-primary-foreground hover:opacity-80 transition-opacity relative" aria-label={`Carrello${totalItems > 0 ? ` (${totalItems} articoli)` : ''}`}>
            <ShoppingCart className="w-4 h-4 md:w-5 md:h-5" />
            {totalItems > 0 && (
              <span className="absolute -top-2 -right-2 bg-secondary text-secondary-foreground text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {totalItems > 99 ? '99+' : totalItems}
              </span>
            )}
          </Link>
        </div>
      </div>
    </header>
  );
};

export const MainHeader = () => {
  return (
    <>
      <TopBar />
      <Header />
    </>
  );
};

export default MainHeader;
